import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BarChart3, Check, Layers3,
  Radio, User, Users, Vote,
} from "lucide-react";
import { canAccessPlatformReservedArea, getDashboardHomePath } from "../utils/roles";
import convegnoHero from "../assets/convegno-hero.png";
import "./platformHome.css";

const IMAGES = {
  live: convegnoHero,
};

const iconProps = { size: 24, strokeWidth: 1.8, "aria-hidden": true };

const PlatformHome = ({ loginStatus }) => {
  const hasDashboard = canAccessPlatformReservedArea(loginStatus);
  const primaryCta = hasDashboard
    ? { to: getDashboardHomePath(loginStatus), label: "Apri la dashboard" }
    : { to: "/ottieni", label: "Richiedi l’attivazione" };

  return (
    <div className="showcase">
      <section className="showcase-hero">
        <div className="showcase-orb showcase-orb--one" aria-hidden="true" />
        <div className="showcase-orb showcase-orb--two" aria-hidden="true" />
        <div className="showcase-hero__inner">
          <div className="showcase-hero__copy">
            <h1 className="showcase-hero__title">
              Webinar professionali.
              <span className="showcase-hero__title-line">Semplici da gestire.</span>
            </h1>
            <p className="showcase-hero__lead">
              Crea e gestisci più piattaforme webinar da un&apos;unica regia: live,
              utenti, sondaggi, accessi e report sempre sotto controllo.
            </p>
            <div className="showcase-hero__actions">
              <Link to={primaryCta.to} className="showcase-btn showcase-btn--primary">
                {primaryCta.label}<ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
            <div className="showcase-proof" aria-label="Punti di forza">
              <span><Check size={16} aria-hidden="true" /> Più piattaforme</span>
              <span><Check size={16} aria-hidden="true" /> Gestione centralizzata</span>
              <span><Check size={16} aria-hidden="true" /> Report automatici</span>
            </div>
          </div>

          <div className="showcase-hero__visual">
            <div className="showcase-stage">
              <div className="showcase-stage__toolbar" aria-hidden="true"><span /><span /><span /><p>live.webinarplus.it</p></div>
              <div className="showcase-stage__screen">
                <img src={IMAGES.live} alt="Sala conferenze durante un webinar in diretta" />
                <span className="showcase-stage__live"><i /> In diretta</span>
                <div className="showcase-stage__caption"><span>Evento in corso</span><strong>Innovazione e futuro degli enti</strong></div>
              </div>
            </div>
            <div className="showcase-float-card showcase-float-card--attendees">
              <span className="showcase-float-card__icon"><User size={18} /></span><span><strong>1.248</strong> partecipanti</span>
            </div>
            <div className="showcase-float-card showcase-float-card--poll">
              <span className="showcase-poll-mini"><i style={{ width: "78%" }} /><i style={{ width: "52%" }} /></span>
              <span><strong>Sondaggio live</strong> Risposte in tempo reale</span>
            </div>
          </div>
        </div>
      </section>

      <div className="showcase-middle" id="potenzialita">
        <section className="showcase-wide-section showcase-wide-section--white">
          <div className="showcase-wide-section__inner">
            <div className="showcase-wide-copy">
              <p className="showcase-kicker">Un unico sistema, più piattaforme</p>
              <h2>Crea uno spazio dedicato per ogni organizzazione.</h2>
              <p>Ogni piattaforma è indipendente, personalizzata e gestibile dalla stessa regia.</p>
            </div>
            <div className="showcase-platform-stack" aria-hidden="true">
              <div className="showcase-platform-card showcase-platform-card--back"><span>03</span><strong>Fondazione Cultura</strong></div>
              <div className="showcase-platform-card showcase-platform-card--middle"><span>02</span><strong>Ordine Professionale</strong></div>
              <div className="showcase-platform-card showcase-platform-card--front">
                <span className="showcase-platform-card__icon"><Layers3 {...iconProps} /></span>
                <div><span>01 · Piattaforma attiva</span><strong>Academy Webinar</strong></div>
                <b>online</b>
              </div>
            </div>
          </div>
        </section>

        <section className="showcase-wide-section showcase-wide-section--ink">
          <div className="showcase-wide-section__inner showcase-wide-section__inner--vertical">
            <div className="showcase-wide-copy showcase-wide-copy--light">
              <p className="showcase-kicker">Tutto ciò che serve</p>
              <h2>Live, utenti, sondaggi.<br />E molto altro ancora.</h2>
            </div>
            <div className="showcase-feature-line">
              <div><span><Radio {...iconProps} /></span><strong>Live</strong></div>
              <div><span><Users {...iconProps} /></span><strong>Utenti</strong></div>
              <div><span><Vote {...iconProps} /></span><strong>Sondaggi</strong></div>
              <div><span><BarChart3 {...iconProps} /></span><strong>Statistiche</strong></div>
            </div>
          </div>
        </section>

        <section className="showcase-wide-section showcase-wide-section--white">
          <div className="showcase-wide-section__inner">
            <div className="showcase-access-panel" aria-hidden="true">
              <div className="showcase-access-panel__head"><span>Accessi in tempo reale</span><strong>124</strong></div>
              <div className="showcase-access-chart"><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /></div>
              <div className="showcase-access-event"><span><User size={18} /></span><div><strong>Maria Rossi</strong><small>Accesso registrato · 10:42</small></div><b>Presente</b></div>
              <div className="showcase-access-event"><span><User size={18} /></span><div><strong>Luca Bianchi</strong><small>Accesso registrato · 10:44</small></div><b>Presente</b></div>
            </div>
            <div className="showcase-wide-copy">
              <p className="showcase-kicker">Presenze certificate</p>
              <h2>Ogni accesso viene tracciato.</h2>
              <p>La piattaforma registra ingressi, uscite e tempo di permanenza durante ogni evento.</p>
            </div>
          </div>
        </section>

      </div>

      <section className="showcase-close">
        <div className="showcase-close__glow" aria-hidden="true" /><p className="showcase-kicker">Il prossimo evento parte da qui</p>
        <h2>La tecnologia resta dietro le quinte.<br />Il tuo evento, al centro.</h2>
        <p>{hasDashboard ? "Entra nell’area riservata e continua a costruire l’esperienza del tuo ente." : "Raccontaci il tuo progetto: configureremo insieme lo spazio più adatto al tuo ente."}</p>
        <Link to={primaryCta.to} className="showcase-btn showcase-btn--light">{primaryCta.label}<ArrowRight size={18} aria-hidden="true" /></Link>
      </section>
    </div>
  );
};

export default PlatformHome;
